/**
 * SCRUM-375 — Preview paginado + impresión real de etiquetas de góndola.
 * Vuelve a resolver los precios en el momento (nunca usa precios congelados
 * del trabajo guardado) contra la misma lista de precios con la que se
 * generó, así "repetir" un trabajo viejo (SCRUM-377) siempre imprime el
 * precio vigente. Requiere un click explícito en "Imprimir" — no autoprintea.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { Button } from "@/components/ui/button";
import { ShelfLabel, type ShelfLabelProduct } from "@/components/shelf-labels/ShelfLabel";
import { LABEL_TEMPLATES, isLabelTemplateKey, type LabelFieldToggles, type LabelTemplateKey } from "@/lib/shelf-labels/templates";
import { computeSheetGeometry, paginateItems, type SheetConfig } from "@/lib/shelf-labels/sheet-layout";
import type { ShelfLabelFilter } from "@/lib/db-service/shelf-labels";

export const Route = createFileRoute("/print/etiquetas/$jobId")({
  validateSearch: (raw: Record<string, unknown>) => ({
    tid: typeof raw.tid === "string" ? raw.tid : "",
  }),
  component: ShelfLabelsPrintPage,
  head: () => ({
    meta: [
      { title: "Etiquetas — Vista de impresión" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

interface PrintJobData {
  id: string;
  priceListId: string | null;
  filters: ShelfLabelFilter;
  templateKey: string;
  sheetConfig: SheetConfig;
  visualParams: { fields?: LabelFieldToggles; discountPct?: number | null } | null;
  productCount: number;
}

function ShelfLabelsPrintPage() {
  const { jobId } = Route.useParams();
  const { tid } = Route.useSearch();
  const [job, setJob] = useState<PrintJobData | null>(null);
  const [products, setProducts] = useState<ShelfLabelProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tid) { setError("Tenant no especificado."); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { job: fetchedJob } = await apiGetJson<{ job?: PrintJobData }>(
          `/api/shelf-labels/print-jobs?id=${jobId}`,
          tid,
        );
        if (!fetchedJob) throw new Error("not_found");
        const { products: resolved } = await apiSendJson<{ products?: ShelfLabelProduct[] }>(
          "/api/shelf-labels/resolve",
          "POST",
          { filter: fetchedJob.filters, priceListId: fetchedJob.priceListId },
          tid,
        );
        if (cancelled) return;
        setJob(fetchedJob);
        setProducts(resolved ?? []);
      } catch {
        if (!cancelled) setError("No se pudo cargar el trabajo de impresión.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, tid]);

  const templateKey: LabelTemplateKey = job && isLabelTemplateKey(job.templateKey) ? job.templateKey : "gondola_clasica";
  const template = LABEL_TEMPLATES[templateKey];
  const fields = job?.visualParams?.fields ?? template.defaultFields;
  const discountPct = job?.visualParams?.discountPct ?? null;

  const geometry = useMemo(() => (job ? computeSheetGeometry(job.sheetConfig) : null), [job]);
  const pages = useMemo(
    () => (geometry ? paginateItems(products, geometry.itemsPerPage) : []),
    [products, geometry],
  );

  if (error) {
    return <div className="p-8 text-center text-red-600">{error}</div>;
  }
  if (loading || !job || !geometry) {
    return <div className="p-8 text-center text-gray-500">Cargando…</div>;
  }

  return (
    <>
      <div className="no-print flex items-center justify-between gap-4 border-b bg-gray-50 px-4 py-3">
        <span className="text-sm font-medium text-gray-700">
          {template.name} — {products.length} etiqueta{products.length === 1 ? "" : "s"} en {pages.length} hoja{pages.length === 1 ? "" : "s"}
          {products.length === 0 && " (sin productos)"}
        </span>
        <Button onClick={() => window.print()} disabled={products.length === 0}>Imprimir</Button>
      </div>

      {products.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground">
          Ningún producto resuelto para este trabajo — puede que hayan sido desactivados o eliminados desde que se generó.
        </p>
      ) : (
        pages.map((pageItems, pageIndex) => (
          <div
            key={pageIndex}
            className="print-sheet"
            style={{
              width: `${geometry.pageWidthMm}mm`,
              height: `${geometry.pageHeightMm}mm`,
              padding: `${job.sheetConfig.marginTopMm}mm ${job.sheetConfig.marginRightMm}mm ${job.sheetConfig.marginBottomMm}mm ${job.sheetConfig.marginLeftMm}mm`,
              display: "grid",
              gridTemplateColumns: `repeat(${job.sheetConfig.columns}, ${geometry.labelWidthMm}mm)`,
              gridTemplateRows: `repeat(${job.sheetConfig.rows}, ${geometry.labelHeightMm}mm)`,
              gap: `${job.sheetConfig.gapVerticalMm}mm ${job.sheetConfig.gapHorizontalMm}mm`,
              boxSizing: "border-box",
              margin: "0 auto",
              background: "white",
            }}
          >
            {pageItems.map((product) => (
              <ShelfLabel
                key={product.productId}
                product={product}
                templateKey={templateKey}
                fields={fields}
                discountPct={discountPct}
              />
            ))}
          </div>
        ))
      )}

      <style>{`
        @page {
          size: ${geometry.pageWidthMm}mm ${geometry.pageHeightMm}mm;
          margin: 0;
        }
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; background: white; }
          .print-sheet { page-break-after: always; }
          .print-sheet:last-child { page-break-after: auto; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        @media screen {
          .print-sheet { margin: 16px auto; box-shadow: 0 0 0 1px rgba(0,0,0,0.1); }
        }
      `}</style>
    </>
  );
}
